/**
 * /account/verify-email — enter the emailed 6-digit verification code.
 *
 * The global AuthChangeRedirector routes every pending verify_email flow here,
 * so this screen serves the self-service email signup (its only producer in
 * practice). It cooperates with that redirector rather than navigating on
 * success itself: while a signup handoff is stashed (see
 * lib/auth/emailVerification.ts) the page puts `?next=<set-password>` into its
 * own URL, and the redirector's LOGGED_IN handling delivers the user there
 * once the code authenticates the session.
 *
 * The email's "Continue signing up" link lands here with `?code=…` prefilled
 * and auto-submits. Opened in a browser without the pending signup session
 * (the classic other-device case), there is no flow to verify — the screen
 * shows the code and says to enter it where the signup was started.
 */
import type { FormEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { useAuth, useAuthInfo } from '@/allauth_auth/hooks.js';
import { resendEmailVerification, verifyEmail } from '@/lib/allauth';
import { SIX_DIGIT_CODE_REGEX } from '@/lib/auth/constants';
import {
  isSafeInAppPath,
  readAndConsumeEmailVerificationAddress,
  readAndConsumeEmailVerificationNextUrl,
} from '@/lib/auth/emailVerification';
import { Button } from '@/lib/base/buttons/button';
import { HintText, useFormValidation } from '@/lib/base/forms';
import { Input } from '@/lib/base/forms/input/input';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { logger } from '@/lib/logger';

import AuthLayout from '../../layouts/AuthLayout';

const CODE_FIELDS = {
  code: {
    required: true,
    validate: (value: unknown) =>
      typeof value === 'string' && SIX_DIGIT_CODE_REGEX.test(value.trim())
        ? undefined
        : 'Enter the 6-digit code from the email',
  },
} as const;

const INVALID_CODE_ERROR =
  "That code didn't work. Check the email and try again.";
const EXPIRED_SESSION_ERROR =
  'This code has expired or was requested somewhere else. Request a new one below, or start over.';
const GENERIC_VERIFY_ERROR =
  "We couldn't check that code. Try again, and get in touch if it keeps happening.";
const RATE_LIMITED_ERROR = 'Give it a minute before trying again.';

export default function VerifyEmailCode() {
  const auth = useAuth();
  const { isAuthenticated, pendingFlow } = useAuthInfo();
  const [searchParams] = useSearchParams();
  const urlCode = searchParams.get('code')?.trim() ?? '';
  const [code, setCode] = useState(urlCode);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const autoSubmittedRef = useRef(false);
  const { getFieldProps, touchAll, isValid } = useFormValidation({
    fields: CODE_FIELDS,
    values: { code },
  });
  const codeField = getFieldProps('code');

  const stashedEmail = readAndConsumeEmailVerificationAddress();
  const nextUrl = readAndConsumeEmailVerificationNextUrl();

  const hasPendingVerification = pendingFlow?.id === 'verify_email';

  const submitCode = useCallback(async (codeValue: string) => {
    setError(null);
    setResendNotice(null);
    setIsSubmitting(true);
    try {
      const resp = await verifyEmail(codeValue.trim());
      if (resp.status === 200 || resp.meta?.is_authenticated) {
        // Verified and logged in — the AuthChangeRedirector's LOGGED_IN
        // handling navigates using this page's ?next (the set-password step).
        return;
      }
      if (resp.status === 429) {
        logger.debug('Verify email rate-limited', { status: resp.status });
        setError(RATE_LIMITED_ERROR);
      } else if (resp.status === 409) {
        logger.debug('Verify email session expired', { status: resp.status });
        setError(EXPIRED_SESSION_ERROR);
      } else if (resp.status === 400) {
        setError(INVALID_CODE_ERROR);
      } else {
        setError(GENERIC_VERIFY_ERROR);
      }
    } catch {
      setError(GENERIC_VERIFY_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // The email link lands here with ?code= — submit it once without requiring
  // another click. Guarded by a ref so it never competes with manual input
  // (same pattern as SignupCode's URL-driven auto-validate).
  useEffect(() => {
    if (
      urlCode &&
      hasPendingVerification &&
      !isAuthenticated &&
      !autoSubmittedRef.current
    ) {
      autoSubmittedRef.current = true;
      void submitCode(urlCode);
    }
  }, [urlCode, hasPendingVerification, isAuthenticated, submitCode]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      touchAll();
      if (!isValid || isSubmitting) {
        return;
      }
      await submitCode(code);
    },
    [touchAll, isValid, isSubmitting, code, submitCode]
  );

  const onResend = useCallback(async () => {
    setError(null);
    setResendNotice(null);
    try {
      const resp = await resendEmailVerification();
      if (resp.status === 200) {
        setResendNotice('We sent a new code. The old one no longer works.');
      } else if (resp.status === 429) {
        logger.debug('Resend email rate-limited', { status: resp.status });
        setError('Give it a minute before requesting another code.');
      } else {
        logger.debug('Resend email failed', { status: resp.status });
        setError(EXPIRED_SESSION_ERROR);
      }
    } catch {
      setError(GENERIC_VERIFY_ERROR);
    }
  }, []);

  // ----- Render (one state machine, no boolean soup) -----

  if (auth === undefined) {
    return (
      <AuthLayout title='Confirm your email'>
        <div className='my-8 flex justify-center'>
          <LoadingIndicator size='md' />
        </div>
      </AuthLayout>
    );
  }

  if (isAuthenticated) {
    // Already verified (or arrived here signed in): continue where the signup
    // handoff points — the same target the LOGGED_IN redirect uses. Prefer the
    // `?next` already on this URL (put there by the pending-verification branch
    // below) over the stored handoff, so the destination survives even if the
    // handoff storage was cleared or is a different tab; fall back to the stored
    // handoff, then the app home. The raw `?next` is attacker-controllable, so
    // it is accepted only when it is a safe in-app path (the stored handoff is
    // already validated on read) — a crafted `//evil.com` cannot redirect off-site.
    const queryNext = searchParams.get('next');
    const target = isSafeInAppPath(queryNext) ? queryNext : (nextUrl ?? '/');
    return <Navigate to={target} replace />;
  }

  if (hasPendingVerification && nextUrl && !searchParams.get('next')) {
    // Put the signup handoff into the URL so the AuthChangeRedirector's
    // LOGGED_IN handling (which reads ?next) lands on the set-password step.
    const params = new URLSearchParams(searchParams);
    params.set('next', nextUrl);
    return <Navigate to={`?${params.toString()}`} replace />;
  }

  if (!hasPendingVerification) {
    return (
      <AuthLayout title='Confirm your email'>
        <div className='my-8 flex flex-col gap-4'>
          {urlCode ? (
            <>
              <p className='text-base text-[var(--color-text-tertiary)]'>
                Looks like you started signing up in a different browser. Enter
                this code there to keep going:
              </p>
              <p className='text-center font-mono text-3xl font-bold tracking-widest text-[var(--color-text-primary)]'>
                {urlCode}
              </p>
            </>
          ) : (
            <p className='text-base text-[var(--color-text-tertiary)]'>
              There's no signup waiting for a code here – it may have expired.
            </p>
          )}
          <p className='text-base text-[var(--color-text-tertiary)]'>
            Need a fresh start?{' '}
            <Link
              to='/get-started'
              className='font-semibold text-fg-brand-primary hover:text-utility-brand-700 transition-colors'
            >
              Create your account
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title='Confirm your email'>
      <div className='my-8 flex flex-col gap-6'>
        <p className='text-base text-[var(--color-text-tertiary)]'>
          {stashedEmail
            ? `We emailed a 6-digit code to ${stashedEmail}.`
            : 'We emailed you a 6-digit code.'}{' '}
          Enter it below to confirm your email address.
        </p>

        <form onSubmit={onSubmit} noValidate className='flex flex-col gap-3'>
          <Input
            type='text'
            inputMode='numeric'
            name='code'
            aria-label='Verification code'
            placeholder='Enter your 6-digit code'
            autoComplete='one-time-code'
            isRequired
            autoFocus
            value={code}
            onChange={setCode}
            className='font-mono text-center text-lg tracking-widest'
            onBlur={codeField.onBlur}
            isInvalid={codeField.showError}
            showError={codeField.showError}
          />
          {codeField.showError && codeField.error && (
            <HintText isInvalid showError>
              {codeField.error}
            </HintText>
          )}
          <Button
            type='submit'
            color='primary'
            size='md'
            className='w-full'
            isDisabled={isSubmitting}
            disabledTooltip='Checking your code…'
            isLoading={isSubmitting}
            showTextWhileLoading
          >
            {isSubmitting ? 'Checking…' : 'Continue'}
          </Button>
          {error && (
            <div role='alert'>
              <HintText isInvalid showError>
                {error}
              </HintText>
            </div>
          )}
          {resendNotice && (
            <p
              role='status'
              className='text-sm text-[var(--color-text-secondary)]'
            >
              {resendNotice}
            </p>
          )}
        </form>

        <div className='flex justify-center gap-1 text-center'>
          <span className='text-sm text-gray-600 dark:text-gray-400'>
            Didn't get it?
          </span>
          <Button color='link-color' size='sm' onClick={onResend}>
            Resend code
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
